import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { getFemaleList, getSearchShelfCards } from 'api';
import {
  FloatButton,
  Icon,
  ListPickupsFemale,
  ListYarikanoGirls,
  Loading,
  Text,
  TextField,
} from 'components';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ESearchTab,
  ESearchShelfType,
  SearchShelfCardSection,
  TFemale,
  TFilterTime,
  TSearchShelfCard,
  TSchedule,
} from 'types';
import CardItem from './CardItem';
import ModalFilter from './ModalFilter';
import ShelfCardsView from './ShelfCardsView';
import ShelfDetailModal from './ShelfDetailModal';
import { hasShelfCardDetail } from './hasShelfCardDetail';
import {
  PAGE_SIZE,
  SESSION_KEYS,
  TEXT_STRING,
  queryKeys,
  routes,
} from '../../constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  claimTrackingSlot,
  getCurrentWeeks,
  getSearchPageViewTrackingKey,
  getShelfTabViewTrackingKey,
  trackSearchPageView,
  trackShelfTabView,
} from 'utils';
import { useAppStore, useUserStore } from 'store';
import { initialFilterFemale } from 'helpers';
import TopRecruitBanner from '../../assets/images/topRecruitBanner.webp';
import { useMessage } from 'hooks';
import StickySearchHeader from 'components/StickySearchHeader';
import { shouldStartShelfDrag } from './shelfDragGuard';

const getShelfSection = (tab: ESearchTab): SearchShelfCardSection | null => {
  if (tab === ESearchTab.JOB_SHELF) return 'JOB';
  if (tab === ESearchTab.PRODUCT_SHELF) return 'PRODUCT';
  return null;
};

const searchTopTabs = [
  {
    title: TEXT_STRING.SEARCH_PAGE.JOB_SHELF,
    id: ESearchTab.JOB_SHELF,
  },
  {
    title: TEXT_STRING.SEARCH_PAGE.PRODUCT_SHELF,
    id: ESearchTab.PRODUCT_SHELF,
  },
  {
    title: TEXT_STRING.SEARCH_PAGE.SCHEDULES_TAB,
    id: ESearchTab.SCHEDULES,
  },
];

const searchSlideTabs = [
  ESearchTab.SCHEDULES,
  ESearchTab.JOB_SHELF,
  ESearchTab.PRODUCT_SHELF,
];

// 最上位タブ (スケジュール / 求人 / 商品) を表す URL パラメータ。
// スケジュールタブ内のサブタブを表す既存の 'tab' とは別物。
const SEARCH_VIEW_PARAM = 'view';

const SEARCH_TAB_BY_VIEW = {
  schedule: ESearchTab.SCHEDULES,
  job: ESearchTab.JOB_SHELF,
  product: ESearchTab.PRODUCT_SHELF,
} as const;

type SearchViewParam = keyof typeof SEARCH_TAB_BY_VIEW;

const SEARCH_VIEW_BY_TAB = Object.fromEntries(
  Object.entries(SEARCH_TAB_BY_VIEW).map(([view, tab]) => [tab, view]),
) as Record<(typeof SEARCH_TAB_BY_VIEW)[SearchViewParam], SearchViewParam>;

const getSearchViewValue = (tab: ESearchTab): SearchViewParam =>
  SEARCH_VIEW_BY_TAB[tab as (typeof SEARCH_TAB_BY_VIEW)[SearchViewParam]] ??
  'schedule';

const getSearchViewTab = (value: string | null): ESearchTab => {
  if (!value) return ESearchTab.SCHEDULES;
  return SEARCH_TAB_BY_VIEW[value as SearchViewParam] ?? ESearchTab.SCHEDULES;
};

const currentWeeks = getCurrentWeeks({});

function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = React.useMemo<ESearchTab>(
    () => (searchParams.get('tab') || ESearchTab.SCHEDULES) as ESearchTab,
    [searchParams],
  );
  const activeSearchTab = React.useMemo<ESearchTab>(
    () => getSearchViewTab(searchParams.get(SEARCH_VIEW_PARAM)),
    [searchParams],
  );
  const isShelfTab = activeSearchTab !== ESearchTab.SCHEDULES;
  const selectedSearchSlideIndex = searchSlideTabs.indexOf(activeSearchTab);
  const initialSearchSlideIndexRef = useRef(selectedSearchSlideIndex);

  // State
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedShelfCard, setSelectedShelfCard] = useState<{
    card: TSearchShelfCard;
    type: ESearchShelfType;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState<TFilterTime>({
    startTime: currentWeeks[0].startDate,
    endTime: currentWeeks[0].startDate,
  });

  // Hook
  const scrollY = useRef<number>(0);
  const lastScrollTopRef = useRef<number>(0);
  const topHeaderRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState<boolean>(true);
  const SCROLL_THRESHOLD = 10;
  const {
    filterFemale,
    setFilterFemale,
    filterOptions,
    franchiseAreas,
    searchScrollY,
    setSearchScrollY,
    overallRankings,
  } = useAppStore();

  const { user, isAuthenticated } = useUserStore();
  const { findOrCreateMgmChannelAndNavigate } = useMessage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tags } = filterOptions || {};
  // ログイン済みで user 未解決の間は計測しない（guest/member セグメント汚染防止）
  const isTrackingReady = !(isAuthenticated && user?.id == null);

  const trackFunnelEntry = useCallback(() => {
    if (!isTrackingReady) return false;
    if (claimTrackingSlot(getSearchPageViewTrackingKey(user?.id))) {
      trackSearchPageView({ userId: user?.id });
    }
    return true;
  }, [isTrackingReady, user?.id]);

  const watchSearchShelfDrag = useCallback(
    (_api: EmblaCarouselType, event: MouseEvent | TouchEvent) => {
      return shouldStartShelfDrag(event.target);
    },
    [],
  );

  const searchShelfCarouselOptions = useMemo(
    () => ({
      loop: true,
      watchDrag: watchSearchShelfDrag,
      startIndex: initialSearchSlideIndexRef.current,
    }),
    [watchSearchShelfDrag],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(searchShelfCarouselOptions);

  const setActiveSearchTab = useCallback(
    (tabId: ESearchTab) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(SEARCH_VIEW_PARAM, getSearchViewValue(tabId));
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const handleSelectShelfCard = useCallback(
    (card: TSearchShelfCard, type: ESearchShelfType) => {
      if (!hasShelfCardDetail(card)) {
        window.open(card.redirectUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      setSelectedShelfCard({ card, type });
    },
    [],
  );

  const setScheduleTab = useCallback(
    (tabId: string) => {
      sessionStorage.setItem(SESSION_KEYS.SEARCH_TAB, tabId);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', tabId);
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const getSelectedFemaleFiltersLabel = useCallback(() => {
    const selectedFranchiseAreas = franchiseAreas
      ?.filter(({ id }) => filterFemale.activityAreas.includes(id))
      .map(({ name }) => name)
      .join(' - ');

    const selectedStyles = tags?.styles
      ?.filter(({ id }) => filterFemale.options.includes(id))
      .map(({ label }) => label)
      .join(' - ');
    const selectedLineage = tags?.lineage
      ?.filter(({ id }) => filterFemale.options.includes(id))
      .map(({ label }) => label)
      .join(' - ');

    const selectedEnjoyMethod = tags?.enjoy_method
      ?.filter(({ id }) => filterFemale.options.includes(id))
      .map(({ label }) => label)
      .join(' - ');

    const selectedAgeRange = tags?.age_range
      ?.filter(({ id }) => filterFemale.options.includes(id))
      .map(({ label }) => label)
      .join(' - ');

    const selectedFilters = [
      selectedFranchiseAreas,
      selectedStyles,
      selectedLineage,
      selectedEnjoyMethod,
      selectedAgeRange,
      filterFemale.keywords,
    ]
      .filter(Boolean)
      .join(', ');
    return selectedFilters;
  }, [filterFemale, franchiseAreas, tags]);

  // Queries
  const {
    data: listFemale,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [queryKeys.FEMALE_LIST, filterFemale, selectedDate, tab],
    queryFn: async ({ pageParam }) => {
      const isTabSchedules = tab === ESearchTab.SCHEDULES;

      return getFemaleList({
        currentPage: pageParam,
        pageSize: PAGE_SIZE,
        filter: {
          ...filterFemale,
          discount: {
            groupCoupon: false,
          },
          schedules: {
            startTime: isTabSchedules ? selectedDate?.startTime : '',
            endTime: isTabSchedules ? selectedDate?.endTime : '',
          },
        },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
    enabled: !isShelfTab,
  });

  const { data: shelfCards, isLoading: isLoadingShelfCards } = useQuery({
    queryKey: [queryKeys.SEARCH_SHELF_CARDS],
    queryFn: getSearchShelfCards,
  });

  // Compute list items using useMemo
  const listItems: TFemale[] = useMemo(() => {
    return listFemale?.pages?.flatMap((p) => p.data ?? []) ?? [];
  }, [listFemale]);

  // Callbacks
  const handleSelectedDate = useCallback((day: TSchedule) => {
    setSelectedDate(() => ({
      startTime: day.startDate,
      endTime: day.startDate,
    }));
  }, []);

  const handleScroll = useCallback((e: MouseEvent) => {
    const scrollTop = (e.target as HTMLElement)?.scrollTop || 0;
    scrollY.current = scrollTop;

    const offsetHeight = topHeaderRef.current?.offsetHeight ?? 0;
    const stickyHeaderHeight = stickyHeaderRef.current?.offsetHeight ?? 0;
    const stickyStart = offsetHeight + stickyHeaderHeight;
    const lastScrollTop = lastScrollTopRef.current;

    if (scrollTop <= stickyStart) {
      setHeaderVisible(true);
    } else if (scrollTop > lastScrollTop + SCROLL_THRESHOLD) {
      setHeaderVisible(false);
    } else if (scrollTop < lastScrollTop - SCROLL_THRESHOLD) {
      setHeaderVisible(true);
    }
    lastScrollTopRef.current = scrollTop;
  }, []);

  // 'view' (上位タブ) を保持したままスケジュール内サブタブだけ切り替える。
  const handleChangeTab = useCallback(
    (tabId: string) => {
      if (tabId === ESearchTab.RANKING) {
        navigate(routes.RANKING);
      } else if (tabId === ESearchTab.HOT_VIDEO) {
        navigate(routes.GAME_PAGE);
      } else {
        setScheduleTab(tabId);
      }
    },
    [navigate, setScheduleTab],
  );

  const searchInput = useCallback(() => {
    return (
      <div onClick={() => setOpen(true)} className="cursor-pointer w-full">
        <TextField
          prefix={
            <div>
              <Icon icon={'searchInput'} color="var(--color-neutral-300)" />
            </div>
          }
          suffix={
            <div
              className="pointer-events-auto"
              onClick={(e) => {
                e.stopPropagation();
                setFilterFemale(initialFilterFemale);
              }}
            >
              <Icon icon={'close'} color="var(--color-neutral-300)" />
            </div>
          }
          name={''}
          value={getSelectedFemaleFiltersLabel()}
          onChange={(_event: React.ChangeEvent<Element>): void => {}}
          placeholder={TEXT_STRING.SEARCH_PAGE.SET_CONDITIONS}
          className="pointer-events-none pl-10px m-1"
          inputClass="truncate"
        />
      </div>
    );
  }, [getSelectedFemaleFiltersLabel, setFilterFemale]);

  const handleFindOrCreateMgmChannelAndNavigate = useCallback(async () => {
    setLoading(true);
    await findOrCreateMgmChannelAndNavigate();
    setLoading(false);
  }, [findOrCreateMgmChannelAndNavigate]);

  // Effect
  useEffect(() => {
    if (searchParams.has('tab')) return;
    const sessionTab = sessionStorage.getItem(SESSION_KEYS.SEARCH_TAB);
    if (sessionTab) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', sessionTab);
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // 棚タブ到達時: タブ存続中 × section 1回（タブボタン・スワイプ双方で activeSearchTab が更新される）
  // tab_view 発火前に page_view スロットも確保し、到達率 > 100% を防ぐ
  useEffect(() => {
    if (!trackFunnelEntry()) return;
    const section = getShelfSection(activeSearchTab);
    if (!section) return;
    if (!claimTrackingSlot(getShelfTabViewTrackingKey(section, user?.id)))
      return;
    trackShelfTabView({ section, userId: user?.id });
  }, [activeSearchTab, trackFunnelEntry, user?.id]);

  // URL のタブとカルーセル位置がずれているときだけ追従させる。
  // ユーザースワイプ由来の URL 更新では snap が既に一致しているため何もしない。
  useEffect(() => {
    if (!emblaApi) return;
    if (emblaApi.selectedScrollSnap() === selectedSearchSlideIndex) return;
    emblaApi.scrollTo(selectedSearchSlideIndex, true);
  }, [emblaApi, selectedSearchSlideIndex]);

  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = (api: EmblaCarouselType) => {
      const nextTab = searchSlideTabs[api.selectedScrollSnap()];
      if (nextTab && nextTab !== activeSearchTab) {
        setActiveSearchTab(nextTab);
      }
    };

    emblaApi.on('select', handleSelect);
    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [activeSearchTab, emblaApi, setActiveSearchTab]);

  return (
    <>
      <div className="flex flex-col h-full overflow-hidden">
        <SearchTopNavigation
          currentTab={activeSearchTab}
          onChangeTab={(tabId) => setActiveSearchTab(tabId)}
        />

        <div className="flex-1 min-h-0 overflow-hidden" ref={emblaRef}>
          <div className="flex h-full items-start">
            <div
              className="flex-[0_0_100%] min-w-0 h-full min-h-0 overflow-y-auto"
              id="scrollFemale"
            >
              <div ref={topHeaderRef}>
                <div className="flex gap-[8px] pt-16px px-8px bg-white items-stretch">
                  <img
                    onClick={() =>
                      window.open(
                        process.env.REACT_APP_SERVICE_URL || '',
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                    src={TopRecruitBanner}
                    alt=""
                    className="w-full h-full object-cover cursor-pointer rounded-xl"
                  />
                </div>
                <ListPickupsFemale />
                <ListYarikanoGirls
                  isActive={
                    isTrackingReady && activeSearchTab === ESearchTab.SCHEDULES
                  }
                  isTrackingReady={isTrackingReady}
                  userId={user?.id}
                />
              </div>

              <StickySearchHeader
                tab={tab}
                onChangeTab={handleChangeTab}
                searchInput={searchInput()}
                selectedDate={selectedDate}
                handleSelectedDate={handleSelectedDate}
                currentWeeks={currentWeeks}
                containerRef={stickyHeaderRef}
                visible={headerVisible}
              />

              {isLoading ||
              queryClient.isFetching({
                queryKey: [queryKeys.FILTER_OPTIONS],
              }) ? (
                <div className="mt-8">
                  <Loading />
                </div>
              ) : (
                <>
                  {!listItems.length ? (
                    <Text
                      bold
                      fontSize={16}
                      className="py-8px flex justify-center"
                    >
                      {TEXT_STRING.COMMON.NO_DATA}
                    </Text>
                  ) : (
                    <InfiniteScroll
                      next={fetchNextPage}
                      hasMore={hasNextPage}
                      loader={
                        <div className="py-[36px] flex justify-center items-center">
                          <Loading />
                        </div>
                      }
                      dataLength={listItems.length}
                      scrollThreshold={0.5}
                      scrollableTarget="scrollFemale"
                      onScroll={handleScroll}
                      initialScrollY={searchScrollY}
                    >
                      <div
                        className={`overflow-x-hidden grid grid-cols-3 gap-x-4px gap-y-12px px-6px py-12px`}
                      >
                        {listItems.map((female) => {
                          return (
                            <CardItem
                              detailHref={routes.DETAIL_FEMALE.replace(
                                ':id',
                                `${female.userId}`,
                              )}
                              onSelect={() => {
                                setSearchScrollY(scrollY?.current);
                              }}
                              data={female}
                              key={female.id}
                              showShiftTime={tab === ESearchTab.SCHEDULES}
                              startDate={selectedDate?.startTime}
                              hasAreaFilter={
                                !!filterFemale.activityAreas?.length
                              }
                              rank={
                                overallRankings.find(
                                  (ranking) =>
                                    ranking.user.id === female.userId,
                                )?.rank
                              }
                            />
                          );
                        })}
                      </div>
                    </InfiniteScroll>
                  )}
                </>
              )}
            </div>

            <div className="flex-[0_0_100%] min-w-0 h-full min-h-0 overflow-y-auto">
              <ShelfCardsView
                isLoading={isLoadingShelfCards}
                cards={shelfCards?.jobShelf || []}
                type="job"
                isActive={
                  isTrackingReady && activeSearchTab === ESearchTab.JOB_SHELF
                }
                isTrackingReady={isTrackingReady}
                onSelectCard={(card) =>
                  handleSelectShelfCard(card, ESearchShelfType.JOB)
                }
                userId={user?.id}
              />
            </div>

            <div className="flex-[0_0_100%] min-w-0 h-full min-h-0 overflow-y-auto">
              <ShelfCardsView
                isLoading={isLoadingShelfCards}
                cards={shelfCards?.productShelf || []}
                type="product"
                isActive={
                  isTrackingReady &&
                  activeSearchTab === ESearchTab.PRODUCT_SHELF
                }
                isTrackingReady={isTrackingReady}
                onSelectCard={(card) =>
                  handleSelectShelfCard(card, ESearchShelfType.PRODUCT)
                }
                userId={user?.id}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact the administrator */}
      <FloatButton
        isLoading={loading}
        content={TEXT_STRING.COMMON.INQUIRY}
        icon={'headphone'}
        handleClick={handleFindOrCreateMgmChannelAndNavigate}
      />
      <ModalFilter
        currentTab={tab}
        open={open}
        onClose={() => setOpen(false)}
        onSearch={(filters) => {
          setFilterFemale({ ...filterFemale, ...filters });
          setOpen(false);
        }}
        filtered={{
          activityAreas: filterFemale.activityAreas,
          options: filterFemale.options,
          keywords: filterFemale.keywords,
        }}
      />
      {selectedShelfCard && (
        <ShelfDetailModal
          card={selectedShelfCard.card}
          type={selectedShelfCard.type}
          onClose={() => setSelectedShelfCard(null)}
        />
      )}
    </>
  );
}

type SearchTopNavigationProps = {
  currentTab: ESearchTab;
  onChangeTab: (tab: ESearchTab) => void;
};

function SearchTopNavigation({
  currentTab,
  onChangeTab,
}: SearchTopNavigationProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
  });

  const updateIndicator = useCallback(() => {
    const activeIndex = searchTopTabs.findIndex(
      (item) => item.id === currentTab,
    );
    const activeTab = tabRefs.current[activeIndex];
    if (!activeTab) return;

    setIndicatorStyle({
      left: activeTab.offsetLeft,
      width: activeTab.offsetWidth,
    });
  }, [currentTab]);

  useEffect(() => {
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => {
      window.removeEventListener('resize', updateIndicator);
    };
  }, [updateIndicator]);

  return (
    <div className="shrink-0 z-[2] bg-white flex items-center justify-center px-16px h-48px border-b border-solid border-neutral-100">
      <div className="relative flex items-center gap-20px h-full">
        <div
          className="pointer-events-none absolute bottom-1.5 h-[2px] bg-neutral-900 transition-all duration-200 ease-out"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />
        {searchTopTabs.map((item, index) => {
          const isActive = currentTab === item.id;
          return (
            <button
              key={item.id}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              type="button"
              aria-current={isActive ? 'page' : undefined}
              className="h-full py-2 text-base font-bold text-neutral-900"
              onClick={() => onChangeTab(item.id)}
            >
              {item.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default Search;
